import { ORPCError } from "@orpc/client";
import { serializeUnknownForLog } from "@repo/utils";
import { isAbortOrCancelledError } from "@shared/lib/react-query/cancelled-error";

interface OrpcClientErrorLog {
	tag: string;
	code: string;
	message: string;
	procedure?: string;
	data?: unknown;
	cause?: unknown;
	issues?: unknown;
	rawKeys?: string[];
}

function extractErrorDetails(error: unknown): OrpcClientErrorLog {
	if (error instanceof ORPCError) {
		const record = error as Error & {
			code?: unknown;
			data?: unknown;
			cause?: unknown;
			issues?: unknown;
		};
		return {
			tag: "ORPCError",
			code: String(record.code ?? "UNKNOWN"),
			message: record.message,
			data: record.data,
			cause: record.cause,
			issues: record.issues,
		};
	}

	if (error instanceof Error) {
		const record = error as Error & {
			code?: unknown;
			data?: unknown;
			cause?: unknown;
			issues?: unknown;
		};
		return {
			tag: error.name || "Error",
			code: String(record.code ?? "UNKNOWN"),
			message: error.message,
			data: record.data,
			cause: record.cause,
			issues: record.issues,
			rawKeys: Object.keys(record),
		};
	}

	return {
		tag: "Unknown",
		code: "UNKNOWN",
		message: String(error),
	};
}

function collectErrorCauseChain(error: unknown, maxDepth = 6): unknown[] {
	const chain: unknown[] = [];
	let current: unknown = error;
	for (let depth = 0; depth < maxDepth; depth += 1) {
		if (!(current instanceof Error) || current.cause == null) {
			break;
		}
		chain.push(current.cause);
		current = current.cause;
	}
	return chain;
}

function isLikelyTransientInfraError(details: OrpcClientErrorLog): boolean {
	if (
		details.code === "SERVICE_UNAVAILABLE" ||
		details.code === "BAD_GATEWAY" ||
		details.code === "GATEWAY_TIMEOUT"
	) {
		return true;
	}
	const messageLower = details.message.toLowerCase();
	if (messageLower.includes("database temporarily unavailable")) {
		return true;
	}
	// Browser/network transport failures (Safari: "Load failed"; Chromium: "Failed to fetch").
	// Align with `isTransientRpcError` — not application bugs; often tab idle / HMR / brief offline.
	if (
		messageLower === "load failed" ||
		messageLower.includes("failed to fetch") ||
		messageLower.includes("networkerror") ||
		messageLower.includes("fetch failed") ||
		messageLower.includes("network request failed")
	) {
		return true;
	}
	if (
		details.tag === "TypeError" &&
		/fetch|network|load failed/i.test(details.message)
	) {
		return true;
	}
	if (details.code !== "INTERNAL_SERVER_ERROR") {
		return false;
	}
	const causeText = JSON.stringify(details.cause ?? "").toLowerCase();
	return (
		causeText.includes("p1001") ||
		causeText.includes("p1002") ||
		causeText.includes("p1017") ||
		causeText.includes("p2024") ||
		causeText.includes("can't reach database") ||
		causeText.includes("database server")
	);
}

function isUnparseableOrpcResponseError(details: OrpcClientErrorLog): boolean {
	return details.message.includes("Cannot parse response body");
}

/** Expected buyer validation outcomes — never trip Next.js console.error overlay. */
const EXPECTED_BUYER_BAD_REQUEST_MESSAGE =
	/^(PROMO_[A-Z0-9_]+|QUOTE_RECIPIENT_MISMATCH|QUOTE_EXPIRED|QUOTE_ALREADY_PAID|QUOTE_REVOKED|QUOTE_SUPERSEDED|QUOTE_CHECKOUT_IN_PROGRESS|QUOTE_BILLING_PERMISSION_REQUIRED)\b/;

/** Idempotent IC graph advance after terminal — soft log only. */
export function isExpectedIcAdvanceAlreadyCompleteError(
	details: Pick<OrpcClientErrorLog, "code" | "message">,
	procedure?: string,
): boolean {
	if (details.code !== "BAD_REQUEST") {
		return false;
	}
	if (!details.message.includes("Attempt already complete")) {
		return false;
	}
	if (!procedure) {
		return true;
	}
	return (
		procedure.includes("advanceInteractionGraph") ||
		procedure.includes("interactiveConversation/advanceInteractionGraph")
	);
}

export function isExpectedBuyerOrpcClientError(
	details: Pick<OrpcClientErrorLog, "code" | "message">,
): boolean {
	if (details.code !== "BAD_REQUEST" && details.code !== "FORBIDDEN") {
		// Client-thrown Error("PROMO_INELIGIBLE") lands as UNKNOWN / Error name.
		return EXPECTED_BUYER_BAD_REQUEST_MESSAGE.test(details.message);
	}
	return EXPECTED_BUYER_BAD_REQUEST_MESSAGE.test(details.message);
}

export function logOrpcClientError(error: unknown, context?: string): void {
	// React Query cancel / AbortSignal / oRPC wrap of abort-as-parse-failure.
	if (isAbortOrCancelledError(error)) {
		return;
	}

	const details = extractErrorDetails(error);
	// Callers (RPCLink onError) must pass the procedure for this call. Do not
	// fall back to a shared "last request" path — concurrent RPCs race it.
	const procedure = context;
	const label = procedure ? `[orpc:${procedure}]` : "[orpc]";
	const serializedIssues =
		details.issues ??
		(details.data as { issues?: unknown } | undefined)?.issues;
	const causeChain = collectErrorCauseChain(error);
	const payload = {
		...details,
		procedure,
		context,
		causeChain: serializeUnknownForLog(causeChain),
		serialized: serializeUnknownForLog({
			data: details.data,
			cause: details.cause,
			issues: serializedIssues,
		}),
	};

	if (isExpectedBuyerOrpcClientError(details)) {
		console.debug(`${label} ${details.code} ${details.message}`, payload);
		return;
	}

	if (isExpectedIcAdvanceAlreadyCompleteError(details, procedure)) {
		console.debug(`${label} ${details.code} ${details.message}`, payload);
		return;
	}

	// Auth races (prefetch / estimate before cookies settle) are expected —
	// do not trip the Next.js error overlay.
	if (details.code === "UNAUTHORIZED") {
		console.debug(`${label} ${details.code} ${details.message}`, payload);
		return;
	}

	if (isLikelyTransientInfraError(details)) {
		console.warn(
			`${label} ${details.code} ${details.message} (transient infra — often after tab idle / DB pool wake-up)`,
			payload,
		);
		return;
	}

	console.error(`${label} ${details.code} ${details.message}`, payload);

	if (isUnparseableOrpcResponseError(details)) {
		console.error(
			`${label} The server returned a non-oRPC body (often HTML/text from Next.js or a redirect). Check Network for this procedure: status, content-type, and response preview.`,
			{ procedure, causeChain: payload.causeChain },
		);
	}

	if (
		details.code === "BAD_REQUEST" &&
		details.message.includes("validation")
	) {
		const issuesText = JSON.stringify(
			serializeUnknownForLog(serializedIssues),
			null,
			2,
		);
		console.warn(`${label} validation issues:\n${issuesText}`);
	}
}
